import { buildAtlAttributionHeader } from '@canvas/atl-attribution';

import { currentDevelopmentUIState } from './dev-ui-state';
import { isRemoteEAPDevMode } from './is-dev-mode';

export interface Board {
  id: string;
  referenceId: string;
  title: string;
}

export type CreatedBoard = Pick<Board, 'id' | 'referenceId'>;

export interface Space {
  id: string;
  name: string;
  key: string;
}

const proxyThroughGateway = (url: string) => {
  if (!isRemoteEAPDevMode()) {
    return url;
  }

  const target = new URL(window.location.origin);
  target.pathname += `/gateway/api/ex/confluence/${currentDevelopmentUIState.cloudId}`;
  target.pathname += url;

  return target.href;
};

const getGraphQLHeaders = (): Record<string, string> => ({
  'Content-Type': 'application/json',
  credentials: 'include',
  'atl-attribution': buildAtlAttributionHeader(
    currentDevelopmentUIState.activationId,
    currentDevelopmentUIState.cloudId,
  ),
});

export async function fetchSpaces(
  spaceNamePattern = currentDevelopmentUIState.containerName,
): Promise<Space[]> {
  const url = proxyThroughGateway('/gateway/api/graphql/slow');
  const response = await fetch(url, {
    method: 'POST',
    headers: getGraphQLHeaders(),
    body: JSON.stringify({
      operationName: 'spacesInSite',
      variables: { spaceNamePattern },
      query: `
        query spacesInSite {
          spaces(spaceNamePattern: "${spaceNamePattern}") {
            nodes {
              id
              name
              key
            }
          }
        }
      `,
    }),
  });

  const { data } = await response.json();

  return data?.spaces?.nodes ?? [];
}

export async function fetchBoards(
  spaceKey = currentDevelopmentUIState.containerKey,
): Promise<Board[]> {
  const url = proxyThroughGateway('/gateway/api/graphql/slow');

  const response = await fetch(url, {
    method: 'POST',
    headers: getGraphQLHeaders(),
    body: JSON.stringify({
      operationName: 'fetchBoards',
      query: `
        query fetchBoards {
          confluence_search(cql: "type=whiteboard and space.key='${spaceKey}'") {
            nodes {
              content {
                id
                referenceId
                title
              }
            }
          }
        }
      `,
    }),
  });

  const { data } = await response.json();
  const nodes = data?.confluence_search?.nodes ?? [];

  // If you landed here you probably dont have access to akishores space in PUG or
  // there has been an issue with the GraphQL query and it returned no data
  // and as a result you cannot be redirected to a board
  if (!response.ok || nodes.length === 0) {
    console.error(
      'You do not have access to this confluence space. See how-to-fix-common-devloop-issues.md for a fix or modify the .env.eap with your own confluence space details',
    );

    return [];
  }

  return nodes.map(({ content }: { content: Board }) => content);
}

export async function createBoard(spaceKey: string): Promise<CreatedBoard | undefined> {
  const url = proxyThroughGateway('/gateway/api/graphql/slow');
  const response = await fetch(url, {
    method: 'POST',
    headers: getGraphQLHeaders(),
    body: JSON.stringify({
      operationName: 'createWhiteboard',
      variables: { spaceKey },
      query:
        'mutation createWhiteboard($spaceKey: String!) {\n  createContentContextual(input: {spaceKey: $spaceKey, status: CURRENT, type: "whiteboard"}) {\n    id\n    referenceId\n    __typename\n  }\n}\n',
    }),
  });

  const { data } = await response.json();
  const board = data?.createContentContextual;

  if (!response.ok || !board?.referenceId) {
    console.error('Could not create a local development whiteboard', data);
    return undefined;
  }

  return board;
}

export async function fetchBoardRestById(contentId: string): Promise<Board | null> {
  const url = `/wiki/rest/api/content/${contentId}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      credentials: 'include',
    },
  });

  if (!response.ok) {
    return null;
  }
  const data = await response.json();

  return {
    id: data.id,
    referenceId: data.referenceId,
    title: data.title,
  };
}
